import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Ban, Link2, Undo2 } from 'lucide-react'
import { announcementsApi, type MemberSummary, type Spelling, type SpellingChoice } from '../../lib/infoserviceApi.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

type Filter = 'unresolved' | 'all'

/**
 * « À rattacher » : chaque écriture d'organisme de la feuille (« CF GUECELARD »,
 * « COUV »…) reçoit une fois pour toutes son adhérent, ou « pas un adhérent ».
 * Le choix vaut pour toutes les annonces de cette écriture, passées et à
 * venir ; il ne change ni les jours ni l'antenne.
 */
export const SpellingsModal = memo(function SpellingsModal({ channelId, members, canManage, onClose, onChanged }: {
  channelId: string
  members: MemberSummary[]
  canManage: boolean
  onClose: () => void
  onChanged: () => void
}) {
  const [spellings, setSpellings] = useState<Spelling[] | null>(null)
  const [filter, setFilter] = useState<Filter>('unresolved')
  const [search, setSearch] = useState('')
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    let cancelled = false
    announcementsApi.members.spellings(channelId)
      .then(list => { if (!cancelled) setSpellings(list) })
      .catch(cause => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Chargement impossible') })
    return () => { cancelled = true }
  }, [channelId])

  const choose = useCallback(async (spelling: Spelling, choice: SpellingChoice) => {
    setBusyKey(spelling.nameKey)
    setError('')
    try {
      setSpellings(await announcementsApi.members.setSpelling(channelId, spelling.label, choice))
      setChanged(true)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Rattachement impossible')
    } finally {
      setBusyKey(null)
    }
  }, [channelId])

  const close = useCallback(() => {
    if (changed) onChanged()
    onClose()
  }, [changed, onChanged, onClose])

  const unresolved = spellings?.filter(spelling => spelling.status === 'UNRESOLVED').length ?? 0
  const visible = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase('fr')
    return (spellings ?? [])
      .filter(spelling => filter === 'all' || spelling.status === 'UNRESOLVED')
      .filter(spelling => !needle || spelling.label.toLocaleLowerCase('fr').includes(needle) || (spelling.memberName ?? '').toLocaleLowerCase('fr').includes(needle))
  }, [filter, search, spellings])

  return (
    <ModalShell title="Écritures de la feuille à rattacher" onClose={close} size="wide" className="spellings-modal">
      <div className="tp-modal__body spellings">
        <p className="announcement-drawer__hint">
          La feuille écrit le même organisme de plusieurs façons. Rattachez chaque écriture à son adhérent une fois pour toutes :
          le choix vaut pour toutes ses annonces, passées et à venir. Une écriture qui n’est pas un adhérent (couverture, régie…)
          se marque « Pas un adhérent ».
        </p>
        <div className="spellings__tools">
          <div className="tp-segmented" role="group" aria-label="Écritures montrées">
            <button type="button" className={`tp-segment${filter === 'unresolved' ? ' is-active' : ''}`} aria-pressed={filter === 'unresolved'} onClick={() => setFilter('unresolved')}>
              À rattacher <span className="announcement-drawer__count">{unresolved}</span>
            </button>
            <button type="button" className={`tp-segment${filter === 'all' ? ' is-active' : ''}`} aria-pressed={filter === 'all'} onClick={() => setFilter('all')}>
              Toutes <span className="announcement-drawer__count">{spellings?.length ?? 0}</span>
            </button>
          </div>
          <input className="tp-input" type="search" placeholder="Écriture ou adhérent…" aria-label="Rechercher une écriture" value={search} onChange={event => setSearch(event.target.value)} />
        </div>
        {error && <div className="tp-feedback tp-feedback--error" role="alert">{error}</div>}

        {!spellings ? <div className="tp-empty-state" aria-live="polite"><Spinner /> Chargement…</div>
          : visible.length === 0 ? (
            <div className="tp-empty-state">{filter === 'unresolved' && !search ? 'Toutes les écritures sont rattachées.' : 'Aucune écriture ne correspond.'}</div>
          ) : (
            <ul className="spellings__list">
              {visible.map(spelling => (
                <SpellingRow key={spelling.nameKey} spelling={spelling} members={members} canManage={canManage}
                  busy={busyKey === spelling.nameKey} disabled={busyKey !== null} onChoose={choose} />
              ))}
            </ul>
          )}

        <div className="tp-modal-actions">
          <button type="button" className="tp-button tp-button--primary" onClick={close}>Fermer</button>
        </div>
      </div>
    </ModalShell>
  )
})

const SpellingRow = memo(function SpellingRow({ spelling, members, canManage, busy, disabled, onChoose }: {
  spelling: Spelling
  members: MemberSummary[]
  canManage: boolean
  busy: boolean
  disabled: boolean
  onChoose: (spelling: Spelling, choice: SpellingChoice) => void
}) {
  const count = `${spelling.announcementCount} annonce${spelling.announcementCount > 1 ? 's' : ''}${spelling.activeCount ? ` · ${spelling.activeCount} en cours` : ''}`
  return (
    <li className={`spellings__item is-${spelling.status.toLowerCase()}`}>
      <span className="spellings__identity">
        <span className="spellings__label">{spelling.label}</span>
        <span className="spellings__meta">{count}</span>
      </span>
      <span className="spellings__state">
        {busy && <Spinner />}
        {spelling.status === 'EXACT' && <span className="spellings__member" title="Même nom que l’adhérent">→ {spelling.memberName}</span>}
        {spelling.status === 'CHOSEN' && <span className="spellings__member">→ {spelling.memberName}</span>}
        {spelling.status === 'IGNORED' && <span className="announcement-drawer__status is-muted">Pas un adhérent</span>}
        {canManage && (spelling.status === 'CHOSEN' || spelling.status === 'IGNORED') && (
          <button type="button" className="tp-button" disabled={disabled} onClick={() => onChoose(spelling, null)}>
            <Undo2 size={14} aria-hidden="true" /> Annuler le choix
          </button>
        )}
        {canManage && spelling.status === 'UNRESOLVED' && spelling.suggestion && (
          <button type="button" className="tp-button tp-button--primary spellings__suggestion" disabled={disabled} title={`Rattacher à ${spelling.suggestion.name}`}
            onClick={() => onChoose(spelling, { memberId: spelling.suggestion!.memberId })}>
            <Link2 size={14} aria-hidden="true" /> <span className="spellings__suggestion-name">{spelling.suggestion.name}</span>
          </button>
        )}
        {canManage && spelling.status !== 'IGNORED' && (
          <select className="tp-input spellings__select" aria-label={`Adhérent de « ${spelling.label} »`} disabled={disabled} value=""
            onChange={event => { if (event.target.value) onChoose(spelling, { memberId: event.target.value }) }}>
            <option value="">{spelling.status === 'UNRESOLVED' ? (spelling.suggestion ? 'Un autre adhérent…' : 'Choisir l’adhérent…') : 'Changer d’adhérent…'}</option>
            {members.filter(member => member.id !== spelling.memberId).map(member => <option key={member.id} value={member.id}>{member.name}</option>)}
          </select>
        )}
        {canManage && spelling.status === 'UNRESOLVED' && (
          <button type="button" className="tp-button" disabled={disabled} onClick={() => onChoose(spelling, { ignored: true })}>
            <Ban size={14} aria-hidden="true" /> Pas un adhérent
          </button>
        )}
      </span>
    </li>
  )
})
